// KontaktInVorgangAufnehmenPopup.tsx — F2 (PO 01.10.2026): „<Kontakt> ist
// nicht im Vorgang <Titel> — mit aufnehmen?" Im kvp-rd-Stil, KEIN
// Browser-Dialog (Edge fragt sonst „leise", und der Nutzer übersieht es).
//
// Ein Vorgang → [Ja] / [Nein]. Mehrere Vorgänge → gesammelt, je Vorgang ein
// Häkchen (vorbelegt an) → [Aufnehmen] / [Nein]. „Nein" ändert nichts: Die
// Zuordnung des Anrufs ist zu diesem Zeitpunkt schon geschrieben.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { vorgangService } from "../../services/vorgangService";
import {
    aufKontaktAbgleichHoeren,
    aufnahmeAuftraege,
    frageEinVorgang,
    zeileMehrereVorgaenge,
    type KontaktAbgleichEintrag,
} from "./kontaktInVorgangAbgleich";
import "../redesign/contactRedesign.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export interface KontaktInVorgangAufnehmenPopupProps {
    eintraege: KontaktAbgleichEintrag[];
    onFertig: () => void;
}

export const KontaktInVorgangAufnehmenPopup: React.FC<KontaktInVorgangAufnehmenPopupProps> = ({
    eintraege,
    onFertig,
}) => {
    const [angehakt, setAngehakt] = useState<Set<number>>(() => new Set(eintraege.map((e) => e.vorgangId)));
    const [arbeitet, setArbeitet] = useState(false);
    const [fehler, setFehler] = useState("");
    const einer = eintraege.length === 1;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !arbeitet) onFertig();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [arbeitet, onFertig]);

    const aufnehmen = async () => {
        const auftraege = aufnahmeAuftraege(eintraege, einer ? new Set([eintraege[0].vorgangId]) : angehakt);
        if (auftraege.length === 0) {
            onFertig();
            return;
        }
        setArbeitet(true);
        setFehler("");
        const ergebnisse = await Promise.allSettled(
            auftraege.map((a) => vorgangService.kontakteAufnehmen(a.vorgangId, a.contactIds)),
        );
        setArbeitet(false);
        if (ergebnisse.some((r) => r.status === "rejected")) {
            setFehler("Nicht jeder Kontakt konnte aufgenommen werden. Die Zuordnung des Anrufs bleibt bestehen.");
            return;
        }
        onFertig();
    };

    return createPortal(
        <div className="kvp-rd kvp-rd-scrim" onClick={arbeitet ? undefined : onFertig}>
            <div
                className="dm"
                role="dialog"
                aria-modal="true"
                aria-label="Kontakt in den Vorgang aufnehmen"
                style={{ width: "min(480px, 100%)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Anruf zugeordnet</div>
                            <h2>{einer ? "Kontakt aufnehmen?" : "Kontakte in die Vorgänge aufnehmen?"}</h2>
                        </div>
                    </div>
                </div>
                <div className="dm-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {einer ? (
                        <div style={{ font: "500 14px/1.45 var(--ui)" }}>{frageEinVorgang(eintraege[0])}</div>
                    ) : (
                        <>
                            <div style={{ font: "500 13px/1.45 var(--ui)", color: "var(--ink-2, #55606b)" }}>
                                Diese Kontakte des Anrufs sind in den Vorgängen noch nicht eingetragen:
                            </div>
                            {eintraege.map((e) => (
                                <label
                                    key={e.vorgangId}
                                    style={{ display: "flex", gap: 8, alignItems: "flex-start", font: "500 13px/1.45 var(--ui)" }}
                                >
                                    <input
                                        type="checkbox"
                                        style={{ accentColor: "var(--aub, #7d2169)", marginTop: 3 }}
                                        checked={angehakt.has(e.vorgangId)}
                                        disabled={arbeitet}
                                        onChange={() =>
                                            setAngehakt((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(e.vorgangId)) next.delete(e.vorgangId);
                                                else next.add(e.vorgangId);
                                                return next;
                                            })
                                        }
                                        aria-label={zeileMehrereVorgaenge(e)}
                                    />
                                    <span>{zeileMehrereVorgaenge(e)}</span>
                                </label>
                            ))}
                        </>
                    )}
                    {fehler && <div className="rcp-err">{fehler}</div>}
                </div>
                <div className="dm-foot">
                    <button type="button" className="rd-pop-btn" onClick={onFertig} disabled={arbeitet}>
                        Nein
                    </button>
                    <button
                        type="button"
                        className="rd-pop-btn primary"
                        onClick={() => void aufnehmen()}
                        disabled={arbeitet || (!einer && angehakt.size === 0)}
                    >
                        {arbeitet ? "Wird aufgenommen…" : einer ? "Ja" : "Aufnehmen"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

/**
 * Die Hülle: hört auf `kontaktAbgleichAnbieten` und zeigt die Rückfragen
 * nacheinander. App-weit einmal gemountet (in der Nach-Anruf-Karte).
 */
export const KontaktInVorgangAufnehmenHost: React.FC = () => {
    const [schlange, setSchlange] = useState<Array<{ nr: number; eintraege: KontaktAbgleichEintrag[] }>>([]);
    const zaehler = useRef(0);
    useEffect(
        () =>
            aufKontaktAbgleichHoeren((eintraege) => {
                zaehler.current += 1;
                const nr = zaehler.current;
                setSchlange((prev) => [...prev, { nr, eintraege }]);
            }),
        [],
    );
    if (schlange.length === 0) return null;
    return (
        <KontaktInVorgangAufnehmenPopup
            key={schlange[0].nr}
            eintraege={schlange[0].eintraege}
            onFertig={() => setSchlange((prev) => prev.slice(1))}
        />
    );
};

export default KontaktInVorgangAufnehmenPopup;
